/***************************************************************************************
 * FileName      : LearningPanel.tsx
 * Description   : 학습 우측 패널 - 장비 개요 + 학습카드 아코디언 (mockup/learning.html 우측 패널 기준)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.09
 * Modified Date : 2026.02.20 개요 카드에 장비 설명 이미지 썸네일 + 클릭 팝업(라이트박스) 추가
 * Reference     : mockup/learning.html (RIGHT PANEL), mockup/asset/js/main.js (LEARNING_CARDS_DATA), doc/equipment-description/, doc/equipment-description/*_test.md
 ***************************************************************************************/

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
// 2026.02.09 : 박은정 : 학습카드 데이터 및 진행 상태 타입 임포트
import { EQUIPMENT_CARDS, CardProgress } from '../services/LearningProgressService';
// 2026.02.09 : 박은정 : 장비 정보 데이터 임포트
import { EQUIPMENT_DATA } from './EquipmentModal';
// 2026.02.10 : 박은정 : 장비 JSON 데이터 서비스 추가 (구성요소 부품 목록을 JSON에서 로드)
import { EquipmentDataService } from '../services/EquipmentDataService';
// 2026.02.11 : 박은정 : 시나리오 서비스 임포트
import { ScenarioService } from '../services/ScenarioService';
// 2026.02.12 : 박은정 : 더미 데이터 생성 유틸리티 (monitoring 카드 + 좌측 패널 공통 사용)
import { buildMonitoringData } from '../utils/monitoringDataGenerator';
// 2026.02.12 : 박은정 : 퀴즈 서비스 임포트 (평가 카드 객관식 문항)
import { QuizService, QuizResult } from '../services/QuizService';
// 2026.02.12 : 박은정 : 모니터링 데이터 타입 임포트 (순환 의존성 해소를 위해 types로 이동)
import { MonitoringData } from '../types/MonitoringData';

// 2026.02.20 : 박은정 : 장비별 설명 이미지 경로 매핑 (assets/image/discription) — 개요 카드에서 표시
const EQUIPMENT_IMAGE_MAP: Record<string, string> = {
  'heat-exchanger': 'assets/image/discription/heat-excahnger.png',
  distillation: 'assets/image/discription/distillation.png',
  circulation: 'assets/image/discription/circulation.png',
};

// 2026.02.09 : 박은정 : 학습카드별 상세 콘텐츠 데이터 (mockup/asset/js/main.js LEARNING_CARDS_DATA 기준)
// 2026.02.12 : 박은정 : 학습 콘텐츠를 doc/equipment-description 기준으로 업데이트 (개요, 공정, 평가)
const LEARNING_CARD_CONTENT: Record<string, Record<string, LearningCardContent>> = {
  'heat-exchanger': {
    // 2026.02.12 : 박은정 : 개요 콘텐츠를 01_heat_exchanger_overview.md 기준으로 업데이트
    overview: {
      title: '장치 개요',
      sections: [
        {
          heading: '학습 목적',
          body: '고온 유체와 저온 유체 사이의 열 교환 현상을 실제 장치에서 관찰하고, 온도·유량·압력 데이터를 기반으로 열전달 이론(LMTD, ε-NTU)을 검증합니다.',
        },
        {
          heading: '학습 목표',
          items: [
            '열전달 원리 체험 — LMTD, ε-NTU 이론 검증',
            '공정 제어 실무 — 인버터 펌프 유량 제어, 3WAY 밸브(TCV-101) BY-PASS 조절',
            '열교환기 유형별 비교 — 판형(PH-103), 쉘앤튜브(HE-104), 침지식 코일(CH-107)',
          ],
        },
        {
          heading: '적용 산업',
          tags: ['정유·석유화학', '발전 플랜트', '식음료·양조', '냉동·냉장', 'HVAC·건축설비', '화학·소재'],
        },
        {
          heading: '핵심 용어',
          terms: [
            { term: '열전달량', def: 'Q = ṁ × Cp × ΔT' },
            { term: 'LMTD', def: '대수평균온도차 — 열교환기 전열면적 산정 핵심' },
            { term: '총괄열전달계수(U)', def: 'Q = U × A × ΔT_lm' },
            { term: '히트펌프', def: '증발기(냉수)·응축기(온수) 동시 생산 사이클' },
            { term: '3WAY 밸브', def: 'TCV-101 — BY-PASS 유량으로 출구온도 제어' },
          ],
        },
      ],
    },
    // 2026.02.12 : 박은정 : 공정 콘텐츠를 01_heat_exchanger_overview.md 기준으로 업데이트
    process: {
      title: '공정 이해',
      sections: [
        {
          heading: '작동 원리',
          body: '히트펌프(CH-107)가 냉수조(TK-101)에서 냉열을 생산하고 응축열로 온수조(TK-105)를 가열합니다. 온수는 PP-106과 TCV-101을 거쳐 PH-103(판형), HE-104(쉘앤튜브) 순서로 열을 방출하고, 냉수는 PP-102(인버터)로 PH-103, HE-104을 거쳐 열을 흡수합니다.',
        },
        {
          heading: '순환 루프',
          items: [
            '온수 루프 — TK-105 → TCV-101(BY-PASS) → PP-106 → PH-103 → HE-104 Tube → TK-105',
            '냉수 루프 — TK-101(증발기코일) → PP-102(인버터) → PH-103 → HE-104 Shell → TK-101',
          ],
        },
        {
          heading: '운전 조건',
          terms: [
            { term: '칠러 온도', def: '-5℃ ~ 10℃ (CH-107)' },
            { term: '인버터 주파수', def: '0 ~ 60Hz (PP-102)' },
            { term: 'TCV 개도율', def: '0 ~ 100% (최소 50% 유지)' },
            { term: '전기히터', def: 'EH-108 — MIN/MAX 자동제어 (30~40℃)' },
          ],
        },
      ],
    },
    // 2026.02.10 : 박은정 : 구성요소 부품 목록을 JSON 데이터에서 동적으로 로드하도록 변경
    components: {
      title: '구성요소',
      sections: [
        {
          heading: '부품 목록',
          parts: [], // JSON 데이터에서 동적 주입 (EquipmentDataService.getComponentParts)
        },
      ],
    },
    // 2026.02.12 : 박은정 : monitoring 카드 → 학습 가이드로 변경 (센서/제어 데이터는 좌측 패널에서 표시)
    monitoring: {
      title: '데이터 모니터링',
      sections: [
        {
          heading: '모니터링 학습 안내',
          body: '좌측 패널에서 실시간 센서 데이터와 제어 장비 상태를 확인할 수 있습니다. 아래 항목을 참고하여 공정 상태를 분석해 보세요.',
        },
      ],
    },
    // 2026.02.13 : 박은정 : 시뮬레이션 목록을 실제 시나리오 JSON 파일 기반으로 업데이트 (폴백 목록)
    scenario: {
      title: '시뮬레이션',
      sections: [
        {
          heading: '시뮬레이션 목록',
          scenarios: [
            { name: '열교환설비 PP-102 수동 운전', duration: '26초' },
            { name: '열교환기 히터부 성능 알아보기', duration: '18초' },
            { name: '열교환기 칠러부 성능 알아보기', duration: '16초' },
          ],
        },
      ],
    },
    // 2026.02.12 : 박은정 : 평가 콘텐츠 — 객관식 15문항 (QuizService에서 문항 로드)
    evaluation: {
      title: '평가',
      sections: [
        {
          heading: '평가 안내',
          body: '객관식 15문항 | 합격 기준 70점(11문항 이상 정답) | 제한시간 없음',
        },
      ],
    },
  },
  distillation: {
    // 2026.02.12 : 박은정 : 개요 콘텐츠를 02_distillation_overview.md 기준으로 업데이트
    overview: {
      title: '장치 개요',
      sections: [
        {
          heading: '학습 목적',
          body: '끓는점 차이를 이용한 기-액 분리 원리를 4단 유리 증류탑(DT-203)에서 직접 관찰합니다. 에탄올(78.3℃)과 증류수(100℃) 혼합용액에서 고순도 에탄올을 분리하는 전체 과정을 시각적으로 체험합니다.',
        },
        {
          heading: '학습 목표',
          items: [
            '분별증류 원리 체험 — 4단 유리 증류탑에서 기-액 접촉 관찰',
            '공정 제어 실습 — HMI 터치패널, 수동/자동운전 모드 전환',
            '공정 데이터 분석 — 온도(TIT), 수위(LIT), 압력(PIT) 실시간 분석',
            '안전 관리 — 알람 추적, 긴급정지(AUTO STOP), 수동 전환 대응',
          ],
        },
        {
          heading: '적용 산업',
          tags: ['석유정제', '석유화학·NCC', '제약·바이오', '식품·주류', '수처리·담수화', '바이오에너지'],
        },
        {
          heading: '핵심 용어',
          terms: [
            { term: '증류', def: '끓는점 차이를 이용한 기-액 분리 단위조작' },
            { term: '분별증류', def: '다단 TRAY에서 기-액 접촉 반복 → 순도 향상' },
            { term: '재비기', def: 'EH-202(5kW) — 보일러 탱크 가열, 증류 구동력' },
            { term: '응축기', def: 'HE-204 — 에탄올 증기(80℃→25℃) 냉각 응축' },
            { term: '환류', def: 'MOV-201 — 응축액을 탑으로 되돌려 분리 효율 향상' },
          ],
        },
      ],
    },
    // 2026.02.12 : 박은정 : 공정 콘텐츠를 02_distillation_overview.md 기준으로 업데이트
    process: {
      title: '공정 이해',
      sections: [
        {
          heading: '작동 원리',
          body: '보일러 탱크(TK-201)에서 EH-202로 80~90℃ 가열 → 증류탑(DT-203) 4단 TRAY에서 기-액 접촉 반복 → 고순도 에탄올 증기가 HE-204 응축기에서 냉각 → KO-205 녹아웃드럼에 수집 → MOV-201로 환류.',
        },
        {
          heading: '증류탑 원리',
          items: [
            '원료(에탄올 60% + 물 40%)를 TK-201에서 80~90℃로 가열·기화',
            '기화 가스가 상승하며 BUBBLE CAP(5개/단)을 통과, 액과 혼합',
            '같은 비점 성분은 해당 단에서 액화 → DOWNCOMER로 하강',
            '저비점 에탄올(78.3℃)은 기체 상태로 상부로 계속 상승',
            '4단 TRAY 통과 후 최상부에서 고순도 에탄올 도달',
            'HE-204 응축기에서 브라인 냉각수로 액화 → KO-205 수집',
          ],
        },
        {
          heading: '자동운전 조건',
          terms: [
            { term: 'EH-202', def: 'MIN(80℃) → ON / MAX(90℃) → OFF (On-Off 제어)' },
            { term: 'CH-208', def: 'SV 설정값 유지 (-5℃ ~ 10℃), PP-206 연동' },
            { term: 'MOV-201', def: 'LIT-202 ≥200mm → OPEN / ≤5mm → CLOSE' },
            { term: 'MOV-202', def: '자동운전 없음 (수동 OPEN/CLOSE만 가능)' },
          ],
        },
      ],
    },
    // 2026.02.10 : 박은정 : 구성요소 부품 목록을 JSON 데이터에서 동적으로 로드하도록 변경
    components: {
      title: '구성요소',
      sections: [
        {
          heading: '부품 목록',
          parts: [], // JSON 데이터에서 동적 주입 (EquipmentDataService.getComponentParts)
        },
      ],
    },
    // 2026.02.12 : 박은정 : monitoring 카드 → 학습 가이드로 변경 (센서/제어 데이터는 좌측 패널에서 표시)
    monitoring: {
      title: '데이터 모니터링',
      sections: [
        {
          heading: '모니터링 학습 안내',
          body: '좌측 패널에서 실시간 센서 데이터와 제어 장비 상태를 확인할 수 있습니다. 아래 항목을 참고하여 공정 상태를 분석해 보세요.',
        },
      ],
    },
    // 2026.02.13 : 박은정 : 시뮬레이션 목록을 실제 시나리오 JSON 파일 기반으로 업데이트 (폴백 목록)
    scenario: {
      title: '시뮬레이션',
      sections: [
        {
          heading: '시뮬레이션 목록',
          scenarios: [
            { name: '증류설비 정상운전 시나리오', duration: '6초' },
          ],
        },
      ],
    },
    // 2026.02.12 : 박은정 : 평가 콘텐츠 — 객관식 15문항 (QuizService에서 문항 로드)
    evaluation: {
      title: '평가',
      sections: [
        {
          heading: '평가 안내',
          body: '객관식 15문항 | 합격 기준 70점(11문항 이상 정답) | 제한시간 없음',
        },
      ],
    },
  },
  circulation: {
    // 2026.02.12 : 박은정 : 개요 콘텐츠를 03_circulation_overview.md 기준으로 업데이트
    overview: {
      title: '장치 개요',
      sections: [
        {
          heading: '학습 목적',
          body: '정제공정 중 혼합/교반 시스템을 LAB SCALE로 구현한 교육용 장치입니다. EDUCTOR & BUBBLE 교반방식으로 용매(물)와 프로세스 가스(공기)의 혼합 및 용해를 시각적으로 구현합니다.',
        },
        {
          heading: '학습 목표',
          items: [
            'EDUCTOR 교반 원리 — 펌프(PP-302) 순환과 EDUCTOR 노즐 부압으로 공기 흡입·혼합',
            'BUBBLE 교반 원리 — 블로워(BL-304)와 산기통(AT-305)으로 미세기포 발생·교반',
            '용존산소(DO) 측정 — OIT-301/302 센서로 6~9ppm → 9ppm 이상 변화 관찰',
            '수동/자동 운전 체험 — LOCAL(인버터 Hz 직접) / REMOTE(유량·압력 SV 자동)',
          ],
        },
        {
          heading: '적용 산업',
          tags: ['수처리·환경', '양식업', '화학·제약', '식품·음료', '오존처리'],
        },
        {
          heading: '핵심 용어',
          terms: [
            { term: 'EDUCTOR', def: 'Venturi 효과로 부압 발생 → 기체/액체 흡입·혼합' },
            { term: 'Venturi 효과', def: '직경 큰 관→작은 관: 유속↑ 압력↓ (베르누이 법칙)' },
            { term: '산기통', def: 'AIR LIFT 작용으로 미세기포 발생 → DO 공급 + 교반' },
            { term: '용존산소(DO)', def: '물에 녹은 산소량 (mg/L), 맑은 물 6~9ppm' },
            { term: 'PV/SV', def: 'PV: 측정 현재값 / SV: 운전자 설정 목표값' },
          ],
        },
      ],
    },
    // 2026.02.12 : 박은정 : 공정 콘텐츠를 03_circulation_overview.md 기준으로 업데이트
    process: {
      title: '공정 이해',
      sections: [
        {
          heading: '작동 원리',
          body: '1계열(EDUCTOR 교반)과 2계열(BUBBLE 교반) 2개의 독립 계열로 구성됩니다. 단독 운전 또는 동시 운전이 가능하며, 각 계열은 인버터 기반 속도 제어로 운전됩니다.',
        },
        {
          heading: '계열별 구성',
          items: [
            '1계열(TK-301) — PP-302 → ED-303(부압 공기흡입) + ED-307(하부 교반) → OIT-301 DO 측정',
            '2계열(TK-306) — BL-304 → AT-305(산기통) → 미세기포 상승·교반 → OIT-302 DO 측정',
          ],
        },
        {
          heading: '운전 조건',
          terms: [
            { term: '1계열 수동', def: '인버터 SV 0~60Hz (PP-302 속도 직접 제어)' },
            { term: '1계열 자동', def: '유량 SV(m³/h) 설정 → 인버터 자동 제어' },
            { term: '2계열 수동', def: '인버터 SV 0~60Hz (BL-304 속도 직접 제어)' },
            { term: '2계열 자동', def: '압력 SV(mmAq) 설정 → 인버터 자동 제어' },
          ],
        },
      ],
    },
    // 2026.02.10 : 박은정 : 구성요소 부품 목록을 JSON 데이터에서 동적으로 로드하도록 변경
    components: {
      title: '구성요소',
      sections: [
        {
          heading: '부품 목록',
          parts: [], // JSON 데이터에서 동적 주입 (EquipmentDataService.getComponentParts)
        },
      ],
    },
    // 2026.02.12 : 박은정 : monitoring 카드 → 학습 가이드로 변경 (센서/제어 데이터는 좌측 패널에서 표시)
    monitoring: {
      title: '데이터 모니터링',
      sections: [
        {
          heading: '모니터링 학습 안내',
          body: '좌측 패널에서 실시간 센서 데이터와 제어 장비 상태를 확인할 수 있습니다. 아래 항목을 참고하여 공정 상태를 분석해 보세요.',
        },
      ],
    },
    // 2026.02.13 : 박은정 : 시뮬레이션 목록을 실제 시나리오 JSON 파일 기반으로 업데이트 (폴백 목록)
    scenario: {
      title: '시뮬레이션',
      sections: [
        {
          heading: '시뮬레이션 목록',
          scenarios: [
            { name: '순환설비 PP-302 수동운전 응답 시험', duration: '28초' },
            { name: '순환설비 BF-304 수동운전 응답 시험', duration: '24초' },
          ],
        },
      ],
    },
    // 2026.02.12 : 박은정 : 평가 콘텐츠 — 객관식 15문항 (QuizService에서 문항 로드)
    evaluation: {
      title: '평가',
      sections: [
        {
          heading: '평가 안내',
          body: '객관식 15문항 | 합격 기준 70점(11문항 이상 정답) | 제한시간 없음',
        },
      ],
    },
  },
};

// 2026.02.09 : 박은정 : 학습카드 콘텐츠 섹션 타입 정의
interface ContentSection {
  heading: string;
  body?: string;
  items?: string[];
  tags?: string[];
  terms?: { term: string; def: string }[];
  // 2026.02.20 : 박은정 : deviceId 필드 추가 (핫스팟 태그 연동용)
  parts?: { deviceId?: string; name: string; role: string; detail: string }[];
  dataRows?: { label: string; value: string; unit: string; desc: string }[];
  scenarios?: { name: string; duration: string }[];
}

interface LearningCardContent {
  title: string;
  sections: ContentSection[];
}

// 2026.02.12 : 박은정 : MonitoringData 타입을 types/MonitoringData.ts로 이동 (순환 의존성 해소)
// 기존 import 경로 호환을 위해 re-export
export type { MonitoringData } from '../types/MonitoringData';

interface Props {
  isVisible: boolean;
  equipmentId: string | null;
  activeCardId?: string;
  cardStatuses: Record<string, CardProgress>;
  onClose: () => void;
  onCardComplete: (equipmentId: string, cardId: string) => void;
  // 2026.02.11 : 박은정 : 아코디언 카드 변경 시 콜백 (모니터 이미지 표시 제어용)
  onCardChange?: (equipmentId: string, cardId: string | null) => void;
  // 2026.02.11 : 박은정 : monitoring 카드 진입 시 데이터 전달 콜백
  onMonitoringDataUpdate?: (equipmentId: string, data: MonitoringData | null) => void;
  // 2026.02.11 : 박은정 : 시나리오 선택 콜백
  onScenarioSelect?: (equipmentId: string, scenarioId: string) => void;
  // 2026.02.20 : 박은정 : 구성요소 부품 클릭 시 핫스팟 태그 표시/숨김 콜백
  onDeviceClick?: (equipmentId: string, deviceId: string) => void;
  // 2026.02.20 : 박은정 : 현재 활성 디바이스 ID (핫스팟 태그 표시 중인 디바이스)
  activeDeviceId?: string | null;
}

// 2026.02.09 : 박은정 : 학습 우측 패널 컴포넌트 (mockup/learning.html 기준)
export const LearningPanel: React.FC<Props> = ({
  isVisible,
  equipmentId,
  activeCardId,
  cardStatuses,
  onClose,
  onCardComplete,
  onCardChange,
  onMonitoringDataUpdate,
  onScenarioSelect,
  onDeviceClick,
  activeDeviceId,
}) => {
  // 2026.02.09 : 박은정 : 현재 펼쳐진 아코디언 카드 ID
  const [expandedCardId, setExpandedCardId] = useState<string | null>(activeCardId || null);
  // 2026.02.20 : 박은정 : 개요 설명 이미지 팝업(라이트박스) 표시 상태
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const openImagePopup = useCallback(() => setIsImagePopupOpen(true), []);
  const closeImagePopup = useCallback(() => setIsImagePopupOpen(false), []);
  // 2026.02.11 : 박은정 : 시나리오 목록 상태
  const [scenarioList, setScenarioList] = useState<Array<{ id: string; name: string; duration: number }>>([]);
  // 2026.02.12 : 박은정 : 퀴즈 상태 관리 (평가 카드용 — 1문항씩 표시)
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const quizService = QuizService.getInstance();

  // 2026.02.11 : 박은정 : 시나리오 목록 로드
  useEffect(() => {
    if (equipmentId) {
      const scenarioService = ScenarioService.getInstance();
      const scenarios = scenarioService.getScenarioList(equipmentId);
      setScenarioList(scenarios);
    } else {
      setScenarioList([]);
    }
    // 2026.02.12 : 박은정 : 장비 변경 시 퀴즈 상태 초기화
    setQuizAnswers({});
    setQuizResult(null);
    setCurrentQuestionIndex(0);
  }, [equipmentId]);

  // 2026.02.09 : 박은정 : activeCardId prop 변경 시 해당 카드를 자동으로 펼침
  // 2026.02.11 : 박은정 : 카드 변경 시 onCardChange 콜백 호출 (모니터 이미지 표시 제어)
  // 2026.02.11 : 박은정 : monitoring 카드 진입 시 더미 값 계산 및 전달
  // 2026.05.14 : 박은정 : monitoring 카드 → components 카드로 통합 → components 카드도 더미 값 전달
  useEffect(() => {
    if (activeCardId) {
      setExpandedCardId(activeCardId);
      if (onCardChange && equipmentId) {
        onCardChange(equipmentId, activeCardId);
      }

      // components 카드 진입 시 모니터링 더미 값 생성·전달 (3D 오버레이 + 좌측 패널 차트 공유)
      if (activeCardId === 'components' && equipmentId && onMonitoringDataUpdate) {
        onMonitoringDataUpdate(equipmentId, buildMonitoringData(equipmentId));
      } else if (onMonitoringDataUpdate && equipmentId) {
        onMonitoringDataUpdate(equipmentId, null);
      }
    } else if (onMonitoringDataUpdate && equipmentId) {
      onMonitoringDataUpdate(equipmentId, null);
    }
  }, [activeCardId, equipmentId]);

  if (!isVisible || !equipmentId) return null;

  const equipment = EQUIPMENT_DATA[equipmentId];
  if (!equipment) return null;

  const cards = EQUIPMENT_CARDS[equipmentId] || [];
  const cardContentMap = LEARNING_CARD_CONTENT[equipmentId] || {};

  // 2026.02.10 : 박은정 : 구성요소 카드의 부품 목록을 JSON 데이터에서 동적으로 주입
  const equipmentDataService = EquipmentDataService.getInstance();
  if (cardContentMap['components']?.sections?.[0]) {
    const jsonParts = equipmentDataService.getComponentParts(equipmentId);
    if (jsonParts.length > 0) {
      cardContentMap['components'].sections[0].parts = jsonParts;
    }
  }

  // 2026.02.12 : 박은정 : monitoring 카드에서 제어 장비 + 계측 장비 상태·값을 동적으로 표시
  // → buildMonitoringData()에서 생성된 예시 데이터를 기반으로 렌더링
  const monitoringDataForCard = buildMonitoringData(equipmentId);
  const controlDevicesForCard = equipmentDataService.getControlDevices(equipmentId);
  const monitoringSectionsForCard = equipmentDataService.getMonitoringData(equipmentId);

  // 2026.02.09 : 박은정 : 카드 상태 텍스트 결정
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'completed': return '완료';
      case 'in-progress': return '진행중';
      default: return '미시작';
    }
  };

  // 2026.02.09 : 박은정 : 카드 상태에 따른 CSS 클래스
  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'completed': return 'lp-card__status--completed';
      case 'in-progress': return 'lp-card__status--in-progress';
      default: return '';
    }
  };

  // 2026.02.09 : 박은정 : 아코디언 토글 핸들러
  // 2026.02.11 : 박은정 : 토글 시 onCardChange 콜백 호출 (모니터 이미지 표시 제어)
  // 2026.02.11 : 박은정 : monitoring 카드 진입 시 더미 값 계산 및 전달
  const handleToggle = (cardId: string) => {
    const newCardId = expandedCardId === cardId ? null : cardId;
    setExpandedCardId(newCardId);

    if (onCardChange && equipmentId) {
      onCardChange(equipmentId, newCardId);
    }

    // 2026.05.14 : 박은정 : components 카드 토글 시 더미 값 전달 (구성+모니터링 통합)
    if (cardId === 'components' && equipmentId && onMonitoringDataUpdate) {
      if (newCardId === 'components') {
        onMonitoringDataUpdate(equipmentId, buildMonitoringData(equipmentId));
      } else {
        onMonitoringDataUpdate(equipmentId, null);
      }
    }
  };

  // 2026.02.12 : 박은정 : 퀴즈 답안 선택 핸들러 (선택 즉시 정답 확인 — 변경 불가)
  const handleQuizAnswer = (questionId: number, choiceIdx: number) => {
    if (quizAnswers[questionId] !== undefined) return; // 이미 답한 문항은 변경 불가
    setQuizAnswers((prev) => ({ ...prev, [questionId]: choiceIdx }));
  };

  // 2026.02.12 : 박은정 : 다음 문제 이동 핸들러
  const handleNextQuestion = () => {
    if (!equipmentId) return;
    const questions = quizService.getQuestions(equipmentId);
    const nextIdx = currentQuestionIndex + 1;
    if (nextIdx < questions.length) {
      setCurrentQuestionIndex(nextIdx);
    } else {
      // 마지막 문항 → 채점 결과 표시
      const result = quizService.gradeQuiz(equipmentId, quizAnswers);
      setQuizResult(result);
    }
  };

  // 2026.02.12 : 박은정 : 퀴즈 다시 풀기 핸들러
  const handleQuizRetry = () => {
    setQuizAnswers({});
    setQuizResult(null);
    setCurrentQuestionIndex(0);
  };

  // 2026.02.12 : 박은정 : 평가 카드 퀴즈 UI 렌더링 (1문항씩 표시 → 정답 확인 → 다음 문제)
  const renderQuiz = () => {
    if (!equipmentId) return null;
    const questions = quizService.getQuestions(equipmentId);
    if (questions.length === 0) return null;

    const totalCount = questions.length;

    // ── 최종 결과 화면 ──
    if (quizResult) {
      return (
        <div className="lp-quiz">
          <div className={`lp-quiz__result ${quizResult.passed ? 'lp-quiz__result--pass' : 'lp-quiz__result--fail'}`}>
            <div className="lp-quiz__result-score">
              <span className="lp-quiz__result-label">점수</span>
              <span className="lp-quiz__result-value">{quizResult.score}점</span>
            </div>
            <div className="lp-quiz__result-detail">
              {quizResult.correct} / {quizResult.total} 문항 정답
            </div>
            <div className={`lp-quiz__result-badge ${quizResult.passed ? 'lp-quiz__result-badge--pass' : 'lp-quiz__result-badge--fail'}`}>
              {quizResult.passed ? '합격' : '불합격'}
            </div>
          </div>

          {/* 전체 문항 요약 (정답/오답 한눈에) */}
          <div className="lp-quiz__summary">
            {questions.map((q) => {
              const detail = quizResult.details.find((d) => d.questionId === q.id);
              return (
                <div
                  key={q.id}
                  className={`lp-quiz__summary-item ${detail?.isCorrect ? 'lp-quiz__summary-item--correct' : 'lp-quiz__summary-item--wrong'}`}
                >
                  <span className="lp-quiz__summary-num">{q.id}</span>
                  <span className="lp-quiz__summary-mark">{detail?.isCorrect ? '○' : '✕'}</span>
                </div>
              );
            })}
          </div>

          <div className="lp-quiz__actions">
            <button className="lp-quiz__retry-btn" onClick={handleQuizRetry} type="button">
              다시 풀기
            </button>
          </div>
        </div>
      );
    }

    // ── 문항 풀기 화면 (1문항씩) ──
    const q = questions[currentQuestionIndex];
    const isAnswered = quizAnswers[q.id] !== undefined;
    const selectedAnswer = quizAnswers[q.id];
    const isCorrect = isAnswered && selectedAnswer === q.answer;

    return (
      <div className="lp-quiz">
        {/* 진행 상황 바 */}
        <div className="lp-quiz__progress">
          <div className="lp-quiz__progress-info">
            <span className="lp-quiz__progress-current">{currentQuestionIndex + 1}</span>
            <span className="lp-quiz__progress-sep">/</span>
            <span className="lp-quiz__progress-total">{totalCount}</span>
          </div>
          <div className="lp-quiz__progress-bar">
            <div
              className="lp-quiz__progress-fill"
              style={{ width: `${((currentQuestionIndex + (isAnswered ? 1 : 0)) / totalCount) * 100}%` }}
            />
          </div>
        </div>

        {/* 현재 문항 */}
        <div className={`lp-quiz__question ${isAnswered ? (isCorrect ? 'lp-quiz__question--correct' : 'lp-quiz__question--wrong') : ''}`}>
          <div className="lp-quiz__q-header">
            <span className="lp-quiz__q-num">{q.id}</span>
            <span className="lp-quiz__q-cat">[{q.category}]</span>
            {isAnswered && (
              <span className={`lp-quiz__q-mark ${isCorrect ? 'lp-quiz__q-mark--correct' : 'lp-quiz__q-mark--wrong'}`}>
                {isCorrect ? '○' : '✕'}
              </span>
            )}
          </div>
          <p className="lp-quiz__q-text">{q.question}</p>
          <div className="lp-quiz__choices">
            {q.choices.map((choice, ci) => {
              const circleNum = ['①', '②', '③', '④'][ci];
              let choiceClass = 'lp-quiz__choice';
              if (selectedAnswer === ci) choiceClass += ' lp-quiz__choice--selected';
              if (isAnswered) {
                if (ci === q.answer) choiceClass += ' lp-quiz__choice--correct';
                else if (selectedAnswer === ci && !isCorrect) choiceClass += ' lp-quiz__choice--wrong';
                // 답변 후에는 클릭 불가 스타일
                choiceClass += ' lp-quiz__choice--disabled';
              }
              return (
                <label
                  key={ci}
                  className={choiceClass}
                  onClick={() => handleQuizAnswer(q.id, ci)}
                >
                  <span className="lp-quiz__choice-num">{circleNum}</span>
                  <span className="lp-quiz__choice-text">{choice}</span>
                </label>
              );
            })}
          </div>

          {/* 해설 (답변 즉시 표시) */}
          {isAnswered && (
            <div className={`lp-quiz__explanation ${isCorrect ? 'lp-quiz__explanation--correct' : 'lp-quiz__explanation--wrong'}`}>
              <span className="lp-quiz__explanation-label">해설</span>
              <span className="lp-quiz__explanation-text">{q.explanation}</span>
            </div>
          )}
        </div>

        {/* 다음 문제 / 결과 확인 버튼 */}
        {isAnswered && (
          <div className="lp-quiz__actions">
            <button className="lp-quiz__submit-btn" onClick={handleNextQuestion} type="button">
              {currentQuestionIndex < totalCount - 1 ? '다음 문제' : '결과 확인'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // 2026.02.12 : 박은정 : monitoring 카드 내 제어 장비 + 계측 장비 데이터 렌더링
  // 2026.05.14 : 박은정 : 클릭된 부품 바로 아래에 해당 디바이스의 제어/센서 값을 인라인으로 표시
  //   - controlDevicesForCard 에서 deviceId 매칭되는 항목의 control 행 표시
  //   - monitoringSectionsForCard 의 dataRows 중 label 이 `{deviceId}-` 로 시작하는 행 표시
  const renderDeviceMonitoringInline = (deviceId: string) => {
    if (!monitoringDataForCard) return null;

    const matchedControlDevice = controlDevicesForCard.find(d => d.deviceId === deviceId);

    // 센서 행 필터: label 이 deviceId 로 시작하는 것 (예: "TIT-101-PV", "PIT-101-PV")
    const matchedSensorRows: Array<{
      heading: string;
      label: string;
      desc?: string;
      unit?: string;
      value: string | number;
    }> = [];
    for (const section of monitoringSectionsForCard) {
      for (const row of section.dataRows) {
        // label 이 `{deviceId}-...` 패턴인지 확인
        if (row.label.startsWith(`${deviceId}-`) || row.label === deviceId) {
          matchedSensorRows.push({
            heading: section.heading,
            label: row.label,
            desc: row.desc,
            unit: row.unit,
            value: monitoringDataForCard.sensorValues?.[row.label] ?? row.value,
          });
        }
      }
    }

    if (!matchedControlDevice && matchedSensorRows.length === 0) return null;

    return (
      <div className="lp-part-inline-monitoring">
        {matchedControlDevice && (
          <div className="lp-data-device">
            {matchedControlDevice.controls && matchedControlDevice.controls.map((ctrl, ci) => {
              const value = monitoringDataForCard.controlValues?.[ctrl.controlId] ?? '-';
              return (
                <div key={ci} className="lp-data-row">
                  <div className="lp-data-row__left">
                    <span className="lp-data-row__label">{ctrl.name}</span>
                  </div>
                  <div className="lp-data-row__right">
                    <span className="lp-data-row__value">
                      <strong>{value}</strong> {ctrl.unit || ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {matchedSensorRows.length > 0 && (
          <div className="lp-section__data">
            {matchedSensorRows.map((row, ri) => (
              <div key={ri} className="lp-data-row">
                <div className="lp-data-row__left">
                  <span className="lp-data-row__label">{row.label}</span>
                  {row.desc && <span className="lp-data-row__desc">{row.desc}</span>}
                </div>
                <div className="lp-data-row__right">
                  <span className="lp-data-row__value">
                    <strong>{row.value}</strong> {row.unit || ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // 2026.02.09 : 박은정 : 콘텐츠 섹션 렌더링
  const renderSection = (section: ContentSection, idx: number) => (
    <div className="lp-section" key={idx}>
      <h4 className="lp-section__title">{section.heading}</h4>

      {section.body && (
        <p className="lp-section__body">{section.body}</p>
      )}

      {section.items && (
        <ul className="lp-section__list">
          {section.items.map((item, i) => (
            <li key={i} className="lp-section__list-item">{item}</li>
          ))}
        </ul>
      )}

      {section.tags && (
        <div className="lp-section__tags">
          {section.tags.map((tag, i) => (
            <span key={i} className="lp-section__tag">{tag}</span>
          ))}
        </div>
      )}

      {section.terms && (
        <div className="lp-section__terms">
          {section.terms.map((t, i) => (
            <div key={i} className="lp-term">
              <span className="lp-term__name">{t.term}</span>
              <span className="lp-term__def">{t.def}</span>
            </div>
          ))}
        </div>
      )}

      {/* 2026.02.20 : 박은정 : 구성요소 부품 클릭 시 핫스팟 태그 표시/숨김 (onDeviceClick 콜백) */}
      {/* 2026.05.14 : 박은정 : 클릭된 부품 바로 아래에 해당 디바이스의 모니터링 값(제어/계측) 인라인 표시 */}
      {section.parts && (
        <div className="lp-section__parts">
          {section.parts.map((p, i) => {
            const isActive = p.deviceId && activeDeviceId === p.deviceId;
            const isClickable = !!p.deviceId && !!onDeviceClick && !!equipmentId;
            return (
              <React.Fragment key={i}>
                <div
                  className={`lp-part${isClickable ? ' lp-part--clickable' : ''}${isActive ? ' lp-part--active' : ''}`}
                  onClick={() => {
                    if (isClickable && p.deviceId) {
                      onDeviceClick(equipmentId!, p.deviceId);
                    }
                  }}
                >
                  <div className="lp-part__header">
                    <span className="lp-part__name">{p.name}</span>
                    <span className="lp-part__role">{p.role}</span>
                  </div>
                  <span className="lp-part__detail">{p.detail}</span>
                </div>
                {isActive && p.deviceId && renderDeviceMonitoringInline(p.deviceId)}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {section.dataRows && (
        <div className="lp-section__data">
          {section.dataRows.map((d, i) => (
            <div key={i} className="lp-data-row">
              <div className="lp-data-row__left">
                <span className="lp-data-row__label">{d.label}</span>
                {d.desc && <span className="lp-data-row__desc">{d.desc}</span>}
              </div>
              <div className="lp-data-row__right">
                <span className="lp-data-row__value">
                  <strong>{d.value}</strong> {d.unit}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {section.scenarios && (
        <div className="lp-section__scenarios">
          {scenarioList.length > 0 ? (
            // 2026.02.11 : 박은정 : 동적으로 로드된 시나리오 목록 표시
            // 2026.02.13 : 박은정 : 시나리오 duration 표시 — 60초 이상이면 분, 미만이면 초로 표시
            scenarioList.map((s, i) => (
              <div
                key={s.id}
                className="lp-scenario lp-scenario--clickable"
                onClick={() => {
                  if (onScenarioSelect && equipmentId) {
                    onScenarioSelect(equipmentId, s.id);
                  }
                }}
              >
                <span className="lp-scenario__num">{i + 1}</span>
                <span className="lp-scenario__name">{s.name}</span>
                <span className="lp-scenario__duration">{s.duration >= 60 ? `${Math.floor(s.duration / 60)}분` : `${s.duration}초`}</span>
              </div>
            ))
          ) : (
            // 2026.02.11 : 박은정 : 기본 시나리오 목록 표시 (로딩 중이거나 시나리오가 없을 때)
            section.scenarios.map((s, i) => (
              <div key={i} className="lp-scenario">
                <span className="lp-scenario__num">{i + 1}</span>
                <span className="lp-scenario__name">{s.name}</span>
                <span className="lp-scenario__duration">{s.duration}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <aside className={`learning-panel${isVisible ? ' learning-panel--visible' : ''}`}>
      {/* 2026.02.09 : 박은정 : 패널 헤더 - 닫기 버튼 */}
      <div className="learning-panel__header">
        <button className="learning-panel__back" onClick={onClose} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* 2026.02.09 : 박은정 : 장비 개요 영역 (mockup .equipment-overview) */}
      <div className="learning-panel__overview">
        <h2 className="learning-panel__eq-name">{equipment.name}</h2>
        <p className="learning-panel__eq-sub">{equipment.nameEn}</p>
        <p className="learning-panel__eq-desc">{equipment.description}</p>
        <div className="learning-panel__specs">
          {equipment.specs.map((spec, idx) => (
            <div className="learning-panel__spec" key={idx}>
              <span className="learning-panel__spec-label">{spec.label}</span>
              <span className="learning-panel__spec-value">{spec.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 2026.02.12 : 박은정 : "학습 시나리오" → "학습 과정"으로 변경 (하위 카드 "시나리오 학습"과 용어 충돌 해소) */}
      <div className="learning-panel__cards">
        <div className="learning-panel__cards-header">
          <h3 className="learning-panel__cards-title">학습 과정</h3>
        </div>

        <div className="learning-panel__cards-list">
          {cards.map((card, idx) => {
            const rawStatus = cardStatuses[card.id]?.status || 'pending';
            const isExpanded = expandedCardId === card.id;
            // 2026.02.11 : 박은정 : 완료된 카드가 펼쳐진 상태이면 in-progress로 표시 (재학습 가능)
            const status = (rawStatus === 'completed' && isExpanded) ? 'in-progress' : rawStatus;
            const content = cardContentMap[card.id];
            const statusText = getStatusText(status);
            const statusClass = getStatusClass(status);

            return (
              <div
                key={card.id}
                className={
                  `lp-card lp-card--${status}`
                  + (isExpanded ? ' lp-card--active' : '')
                }
              >
                {/* 카드 헤더 (클릭하여 토글) */}
                <div className="lp-card__header" onClick={() => handleToggle(card.id)}>
                  <div className="lp-card__left">
                    <span className="lp-card__number">
                      {status === 'completed' ? '✓' : idx + 1}
                    </span>
                    <span className="lp-card__title">{card.label}</span>
                  </div>
                  <div className="lp-card__right">
                    <span className={`lp-card__status ${statusClass}`}>{statusText}</span>
                    <svg
                      className="lp-card__chevron"
                      width="16" height="16"
                      viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* 카드 본문 (아코디언) */}
                <div className="lp-card__content">
                  <div className="lp-card__body">
                    {content ? (
                      <>
                        {/* 2026.02.20 : 박은정 : 개요 카드일 때 장비 설명 이미지 썸네일 표시 (클릭 시 팝업 확대) */}
                        {card.id === 'overview' && equipmentId && EQUIPMENT_IMAGE_MAP[equipmentId] && (
                          <div className="lp-overview-thumb-wrap">
                            <img
                              className="lp-overview-thumb"
                              src={EQUIPMENT_IMAGE_MAP[equipmentId]}
                              alt={`${equipment.name} 개요 이미지`}
                              onClick={openImagePopup}
                            />
                            <span className="lp-overview-thumb-hint">클릭하여 크게 보기</span>
                          </div>
                        )}

                        {content.sections.map((sec, si) => renderSection(sec, si))}

                        {/* 2026.05.14 : 박은정 : components 카드는 부품 클릭 시 renderDeviceMonitoringInline 으로 인라인 표시 (renderSection 내부 처리) */}

                        {/* 2026.02.12 : 박은정 : 평가 카드일 때 객관식 퀴즈 렌더링 */}
                        {card.id === 'evaluation' && renderQuiz()}

                        {/* 2026.02.09 : 박은정 : 학습 완료 버튼 — 클릭 시 다음 카드 자동 펼침 */}
                        {status !== 'completed' && (
                          <div className="lp-card__action">
                            <button
                              className="lp-card__complete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCardComplete(equipmentId, card.id);
                                // 2026.02.09 : 박은정 : 다음 학습 카드가 있으면 자동으로 펼침
                                // 2026.02.11 : 박은정 : 다음 카드 전환 시 onCardChange 콜백 호출
                                const nextCard = cards[idx + 1];
                                if (nextCard) {
                                  setExpandedCardId(nextCard.id);
                                  if (onCardChange) onCardChange(equipmentId, nextCard.id);
                                } else {
                                  setExpandedCardId(null); // 마지막 카드면 접기
                                  if (onCardChange) onCardChange(equipmentId, null);
                                }
                              }}
                              type="button"
                            >
                              {card.label} 학습 완료
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="lp-section__body">콘텐츠 준비 중...</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* 2026.02.20 : 박은정 : 이미지 팝업 (라이트박스) — React Portal로 document.body에 렌더링하여 화면 중앙 표시 */}
      {isImagePopupOpen && equipmentId && EQUIPMENT_IMAGE_MAP[equipmentId] && ReactDOM.createPortal(
        <div className="lp-lightbox" onClick={closeImagePopup}>
          <button className="lp-lightbox__close" onClick={closeImagePopup}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <img
            className="lp-lightbox__img"
            src={EQUIPMENT_IMAGE_MAP[equipmentId]}
            alt={`${equipment.name} 개요 확대`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </aside>
  );
};

